'use client';

import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle } from 'lucide-react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content: string;
  children?: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  trigger?: 'hover' | 'click';
  icon?: boolean;
}

export default function Tooltip({
  content,
  children,
  position = 'top',
  className = '',
  trigger = 'hover',
  icon = false
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [actualPosition, setActualPosition] = useState(position);
  const [mounted, setMounted] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Calculate position using fixed coordinates
  const calculatePosition = () => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    let bestPosition = position;
    const tooltipHeight = 40; // Approximate height
    const tooltipWidth = 200; // Approximate width
    const gap = 8;

    // Check if tooltip goes outside viewport and adjust
    if (position === 'top' && triggerRect.top - tooltipHeight - gap < 10) {
      bestPosition = 'bottom';
    } else if (position === 'bottom' && triggerRect.bottom + tooltipHeight + gap > viewport.height - 10) {
      bestPosition = 'top';
    } else if (position === 'left' && triggerRect.left - tooltipWidth - gap < 10) {
      bestPosition = 'right';
    } else if (position === 'right' && triggerRect.right + tooltipWidth + gap > viewport.width - 10) {
      bestPosition = 'left';
    }

    setActualPosition(bestPosition);

    // Calculate fixed position based on trigger element
    let top = 0;
    let left = 0;

    switch (bestPosition) {
      case 'top':
        top = triggerRect.top - gap;
        left = triggerRect.left + triggerRect.width / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + gap;
        left = triggerRect.left + triggerRect.width / 2;
        break;
      case 'left':
        top = triggerRect.top + triggerRect.height / 2;
        left = triggerRect.left - gap;
        break;
      case 'right':
        top = triggerRect.top + triggerRect.height / 2;
        left = triggerRect.right + gap;
        break;
    }

    // Clamp left position to prevent overflow
    const minLeft = tooltipWidth / 2 + 10;
    const maxLeft = viewport.width - tooltipWidth / 2 - 10;
    left = Math.max(minLeft, Math.min(maxLeft, left));

    setTooltipStyle({ top, left });
  };

  useEffect(() => {
    if (isVisible) {
      calculatePosition();
    }
  }, [isVisible]);

  const handleShow = () => {
    setIsVisible(true);
  };

  const handleHide = () => {
    setIsVisible(false);
  };

  const handleToggle = () => {
    setIsVisible(!isVisible);
  };

  const triggerProps = trigger === 'hover' 
    ? { onMouseEnter: handleShow, onMouseLeave: handleHide }
    : { onClick: handleToggle };

  const getTooltipClasses = () => {
    const baseClasses = "fixed z-[9999] px-3 py-2 text-sm font-medium text-white bg-neutral-900 rounded-lg shadow-lg max-w-xs pointer-events-none";
    const positionClasses = {
      top: "-translate-x-1/2 -translate-y-full",
      bottom: "-translate-x-1/2", 
      left: "-translate-x-full -translate-y-1/2",
      right: "-translate-y-1/2"
    };
    
    return `${baseClasses} ${positionClasses[actualPosition]}`;
  };

  const getArrowClasses = () => {
    const arrowClasses = {
      top: "absolute top-full left-1/2 -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent border-t-neutral-900",
      bottom: "absolute bottom-full left-1/2 -translate-x-1/2 border-l-4 border-r-4 border-b-4 border-transparent border-b-neutral-900",
      left: "absolute left-full top-1/2 -translate-y-1/2 border-t-4 border-b-4 border-l-4 border-transparent border-l-neutral-900",
      right: "absolute right-full top-1/2 -translate-y-1/2 border-t-4 border-b-4 border-r-4 border-transparent border-r-neutral-900"
    };
    
    return arrowClasses[actualPosition];
  };

  const tooltipContent = isVisible && mounted ? (
    <>
      {trigger === 'click' && (
        <div 
          className="fixed inset-0 z-[9998]" 
          onClick={handleHide}
        />
      )}
      <div
        className={getTooltipClasses()}
        style={{ ...tooltipStyle, whiteSpace: 'nowrap' }}
      >
        {content}
        <div className={getArrowClasses()} />
      </div>
    </>
  ) : null;

  return (
    <div className={`relative inline-block ${className}`} ref={triggerRef}>
      <div {...triggerProps}>
        {children || (icon && <HelpCircle size={16} className="text-gray-400 hover:text-gray-600 cursor-help" />)}
      </div>
      {mounted && tooltipContent && createPortal(tooltipContent, document.body)}
    </div>
  );
}

// Export for use with icons
export function TooltipIcon({ content, className = '' }: { content: string; className?: string }) {
  return (
    <Tooltip content={content} icon className={className} />
  );
}
